import React, { useState, useEffect, useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { formatMoney, formatDateShort, toCents } from "@/lib/format";
import { PAYMENT_METHODS } from "@/lib/bookingConstants";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FieldSelect } from "@/components/ui/field-select";
import { ReceiptField } from "@/components/finance/ReceiptField";
import { Loader2, Check } from "lucide-react";

const input = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20";
const label = "mb-1 block text-sm font-medium text-slate-700";
const METHOD_OPTS = [
  { value: "cash", label: "Espèces (impacte la caisse)" },
  { value: "transfer", label: "Virement bancaire" },
  { value: "card", label: "Carte" },
  { value: "other", label: "Autre" },
];

// Group/unitary/partial settlement. Selecting no commission settles across ALL due (FIFO).
export function SettlementDialog({ open, onOpenChange, partner, dueBookings, currency }) {
  const { orgId } = useAuth();
  const qc = useQueryClient();
  const [method, setMethod] = useState("cash");
  const [selected, setSelected] = useState({});
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));

  const list = useMemo(() => dueBookings || [], [dueBookings]);
  useEffect(() => {
    if (!open) return;
    setMethod("cash"); setSelected({}); setNote(""); setReceipt(null);
    setDate(new Date().toISOString().slice(0, 10));
  }, [open]);

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const totalAll = useMemo(() => list.reduce((s, b) => s + b.commission_remaining_cents, 0), [list]);
  const totalSelected = useMemo(() => list.filter((b) => selected[b.id]).reduce((s, b) => s + b.commission_remaining_cents, 0), [list, selected]);
  const cap = selectedIds.length ? totalSelected : totalAll;

  useEffect(() => { setAmount(cap ? String(cap / 100) : ""); }, [cap]);

  const amountCents = toCents(amount);
  const overCap = amountCents > cap;
  const canSubmit = amountCents > 0 && !overCap && cap > 0;

  const mut = useMutation({
    mutationFn: async () => (await api.post("/api/settlements", {
      organization_id: orgId, partner_id: partner.id, payment_method: method,
      amount_cents: amountCents, settlement_date: date,
      booking_ids: selectedIds, receipt_file_id: receipt?.id || null, note: note || null,
    })).data,
    onSuccess: (d) => { toast.success(`Règlement ${d.settlement_reference} enregistré`); qc.invalidateQueries(); onOpenChange(false); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail) || e.message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="settlement-dialog">
        <DialogHeader><DialogTitle>Régler une commission — {partner?.name}</DialogTitle>
          <DialogDescription className="sr-only">Sélectionner des commissions dues et enregistrer un règlement.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="mb-1 flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-slate-500">
              <span>Commissions dues</span><span data-testid="settlement-total-due">Solde total : {formatMoney(totalAll, currency)}</span>
            </div>
            {list.length === 0 ? <p className="py-2 text-sm text-slate-400">Aucune commission due.</p> : (
              <div className="max-h-48 space-y-1 overflow-y-auto">
                {list.map((b) => {
                  const on = !!selected[b.id];
                  return (
                    <div key={b.id} className={`flex items-center gap-2 rounded-lg border p-2 text-sm ${on ? "border-primary bg-emerald-50" : "border-slate-200 bg-white"}`}>
                      <button type="button" onClick={() => setSelected((s) => ({ ...s, [b.id]: !s[b.id] }))} data-testid={`settle-select-${b.id}`}
                        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${on ? "border-primary bg-primary text-white" : "border-slate-300"}`}>{on && <Check className="h-3.5 w-3.5" />}</button>
                      <div className="min-w-0 flex-1"><p className="font-mono text-xs text-slate-700">{b.booking_reference}</p><p className="text-xs text-slate-400">{formatDateShort(b.booking_date)}</p></div>
                      <span className="font-mono text-slate-800">{formatMoney(b.commission_remaining_cents, currency)}</span>
                    </div>
                  );
                })}
              </div>
            )}
            <p className="mt-1 text-xs text-slate-400">{selectedIds.length ? `${selectedIds.length} sélectionnée(s) · ${formatMoney(totalSelected, currency)}` : "Aucune sélection → règlement FIFO sur toutes les commissions dues"}</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><label className={label}>Mode de paiement</label><FieldSelect testId="settlement-method" className="w-full" value={method} onChange={setMethod} options={METHOD_OPTS} /></div>
            <div><label className={label}>Date</label><input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="settlement-date" className={input} /></div>
          </div>
          <div>
            <label className={label}>Montant à régler ({currency})</label>
            <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="settlement-amount" className={input} />
            <p className="mt-1 text-xs text-slate-400">Maximum réglable : {formatMoney(cap, currency)} {amountCents < cap && amountCents > 0 ? "· règlement partiel" : ""}</p>
            {overCap && <p className="mt-1 text-xs text-rose-600" data-testid="settlement-overcap">Le montant dépasse le solde dû.</p>}
          </div>
          <div><label className={label}>Justificatif (optionnel)</label><ReceiptField value={receipt} onChange={setReceipt} /></div>
          <div><label className={label}>Note (optionnel)</label><input value={note} onChange={(e) => setNote(e.target.value)} data-testid="settlement-note" className={input} /></div>

          <button onClick={() => mut.mutate()} disabled={!canSubmit || mut.isPending} data-testid="save-settlement-button"
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Enregistrer le règlement — {formatMoney(amountCents, currency)}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function SettlementDetailDialog({ settlement, open, onOpenChange, currency }) {
  const { orgId } = useAuth();
  const s = settlement;
  const viewReceipt = async () => {
    try {
      const { data } = await api.get(`/api/files/${s.receipt_file_id}`, { params: { organization_id: orgId }, responseType: "blob" });
      window.open(URL.createObjectURL(data), "_blank", "noopener");
    } catch { toast.error("Impossible d'ouvrir le justificatif"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md" data-testid="settlement-detail-dialog">
        <DialogHeader><DialogTitle className="font-mono">{s?.settlement_reference}</DialogTitle>
          <DialogDescription className="sr-only">Détail du règlement de commission.</DialogDescription></DialogHeader>
        {s && (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <Field k="Montant" v={<span className="font-mono font-semibold text-emerald-700">{formatMoney(s.amount_cents, currency)}</span>} />
              <Field k="Mode" v={PAYMENT_METHODS[s.payment_method] || s.payment_method} />
              <Field k="Date" v={formatDateShort(s.settlement_date)} />
              <Field k="Enregistré par" v={s.created_by_name || "—"} />
              <Field k="Dépense liée" v={<span className="font-mono">{s.expense_reference}</span>} />
              <Field k="Impact caisse" v={s.affects_cash ? "Oui (espèces)" : "Non"} />
            </div>
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-500">Commissions couvertes</p>
              <div className="space-y-1">
                {(s.allocations || []).map((a) => (
                  <div key={a.booking_id} className="flex justify-between rounded-lg border border-slate-200 bg-white p-2" data-testid={`settlement-alloc-${a.booking_id}`}>
                    <span className="font-mono text-xs text-slate-700">{a.booking_reference}</span>
                    <span className="font-mono text-slate-800">{formatMoney(a.allocated_cents, currency)}</span>
                  </div>
                ))}
              </div>
            </div>
            {s.note && <Field k="Note" v={s.note} />}
            {s.receipt_file_id && <button onClick={viewReceipt} data-testid="settlement-view-receipt" className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-primary hover:bg-emerald-50">Voir le justificatif</button>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ k, v }) => (<div><p className="text-xs text-slate-400">{k}</p><p className="font-medium text-slate-800">{v}</p></div>);
